import React, {useEffect, useState, useCallback, useRef} from 'react';
import {apiClient} from '../api/api';
import { 
  RefreshCw, 
  Filter, 
  Calendar, 
  DollarSign, 
  Package, 
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  Check,
  
} from 'lucide-react';
import './RefundOrders.css';
import GradientHeading from './GradientHeading';
import Loader from './Loader';


// Return status mapping with short labels and descriptions
const returnStatusMap = {
    'RETURN_OR_REFUND_REQUEST_PENDING': {
        label: 'Pending Review',
        description: 'Buyer has initiated a return or refund request. The request is pending review by seller or platform.',
        type: 'pending'
    },
    'REFUND_OR_RETURN_REQUEST_REJECT': {
        label: 'Rejected',
        description: 'The return or refund request was rejected.',
        type: 'rejected'
    },
    'AWAITING_BUYER_SHIP': {
        label: 'Awaiting Return',
        description: 'The return request was approved. The seller is waiting for the buyer to ship the approved items to the seller.',
        type: 'processing'
    },
    'BUYER_SHIPPED_ITEM': {
        label: 'Item Shipped',
        description: 'Buyer has shipped the approved items to seller.',
        type: 'processing'
    },
    'REJECT_RECEIVE_PACKAGE': {
        label: 'Package Rejected',
        description: 'Seller inspected the returned items and rejected the return package.',
        type: 'rejected'
    },
    'RETURN_OR_REFUND_REQUEST_SUCCESS': {
        label: 'Approved',
        description: 'The return/refund request was approved. The buyer will be refunded.',
        type: 'approved'
    },
    'RETURN_OR_REFUND_REQUEST_CANCEL': {
        label: 'Cancelled',
        description: 'The request has been cancelled by the buyer or system.',
        type: 'cancelled'
    },
    'RETURN_OR_REFUND_REQUEST_COMPLETE': {
        label: 'Completed',
        description: 'The return/refund was processed successfully. The buyer has been refunded.',
        type: 'completed'
    },
    'REPLACEMENT_REQUEST_PENDING': {
        label: 'Replacement Pending',
        description: 'Buyer has initiated a replacement request. The request is pending review by seller.',
        type: 'pending'
    },
    'REPLACEMENT_REQUEST_REJECT': {
        label: 'Replacement Rejected',
        description: 'Seller reject the buyer replacement request.',
        type: 'rejected'
    },
    'REPLACEMENT_REQUEST_REFUND_SUCCESS': {
        label: 'Refunded (No Stock)',
        description: 'Buyer\'s replacement request was resolved by refund due to insufficient inventory.',
        type: 'approved'
    },
    'REPLACEMENT_REQUEST_CANCEL': {
        label: 'Replacement Cancelled',
        description: 'Buyer canceled the replacement request.',
        type: 'cancelled'
    },
    'REPLACEMENT_REQUEST_COMPLETE': {
        label: 'Replacement Complete',
        description: 'Seller has approved the buyer\'s replacement request, platform will generate a new order for seller to fulfill.',
        type: 'completed'
    },
    'AWAITING_BUYER_RESPONSE': {
        label: 'Awaiting Response',
        description: 'Seller offer another return type to the buyer, and waiting buyer response.',
        type: 'pending'
    }
};


const RefundOrders = () => {
    const [refunds, setRefunds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState('');
    const [statistics, setStatistics] = useState({});
    const [processingActions, setProcessingActions] = useState({});
    // ...existing code...
    const isInitialMount = useRef(true);

    // Rejection modal states
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectingRefundId, setRejectingRefundId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    // Approval modal states
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [approvingRefundId, setApprovingRefundId] = useState(null);
    const [buyerKeepItem, setBuyerKeepItem] = useState(null);

    // Filter states
    const [dateRange, setDateRange] = useState('yesterday');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [selectedReturnTypes, setSelectedReturnTypes] = useState(['REFUND', 'RETURN_AND_REFUND', 'REPLACEMENT']);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const returnTypeOptions = [
        { value: 'REFUND', label: 'Refund Only' },
        { value: 'RETURN_AND_REFUND', label: 'Return & Refund' },
        { value: 'REPLACEMENT', label: 'Replacement' }
    ];

    const fetchRefunds = useCallback(async (isInitialLoad = false) => {
        if (isInitialLoad) {
            setLoading(true);
        } else {
            setFetching(true);
        }
        setError('');
        try {
            // Helper function to get date range based on selection
            const getDateRange = () => {
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                
                const lastWeek = new Date(today);
                lastWeek.setDate(today.getDate() - 7);
                
                const lastMonth = new Date(today);
                lastMonth.setMonth(today.getMonth() - 1);

                const formatDate = (date) => date.toISOString().split('T')[0];

                switch (dateRange) {
                    case 'yesterday':
                        return {
                            start_date: formatDate(yesterday),
                            end_date: formatDate(yesterday)
                        };
                    case 'last_week':
                        return {
                            start_date: formatDate(lastWeek),
                            end_date: formatDate(yesterday) // End date is yesterday, not today
                        };
                    case 'last_month':
                        return {
                            start_date: formatDate(lastMonth),
                            end_date: formatDate(yesterday) // End date is yesterday, not today
                        };
                    case 'custom':
                        return {
                            start_date: customStartDate,
                            end_date: customEndDate
                        };
                    default:
                        return {
                            start_date: formatDate(lastWeek),
                            end_date: formatDate(yesterday) // End date is yesterday, not today
                        };
                }
            };

            const dateParams = getDateRange();
            const params = new URLSearchParams();
            
            // Add date range parameters
            if (dateParams.start_date) params.append('start_date', dateParams.start_date);
            if (dateParams.end_date) params.append('end_date', dateParams.end_date);
            
            // Add return type filters
            selectedReturnTypes.forEach(type => {
                params.append('return_types', type);
            });

            const res = await apiClient.get(`/refunds?${params.toString()}`, {
                credentials: 'include', 
                withCredentials: true
            });
            
            // Update based on the new API response structure
            const data = res.data?.data || {};
            const returnOrders = data.return_orders || [];
            const stats = data.statistics || {};
            
            setRefunds(returnOrders);
            setStatistics(stats);
        } catch (err) {
            console.error('Error fetching refunds:', err, err?.response);
            setError(err.response?.data?.detail || err.message || 'Failed to fetch refunds');
        } finally {
            setLoading(false);
            setFetching(false);
        }
    }, [dateRange, customStartDate, customEndDate, selectedReturnTypes]);

    useEffect(() => {
        if (isInitialMount.current) {
            fetchRefunds(true); // Initial load
            isInitialMount.current = false;
        } else {
            fetchRefunds(false); // Subsequent loads
        }
    }, [dateRange, customStartDate, customEndDate, selectedReturnTypes, fetchRefunds]);

    // Set the browser tab title
    useEffect(() => {

    }, []);

    // Handle clicking outside dropdown
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [dropdownOpen]);

    const handleReturnTypeChange = (type) => {
        setSelectedReturnTypes(prev => 
            prev.includes(type) 
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    const handleSelectAllReturnTypes = () => {
        setSelectedReturnTypes(returnTypeOptions.map(option => option.value));
    };

    const handleClearAllReturnTypes = () => {
        setSelectedReturnTypes([]);
    };

    const getDropdownDisplayText = () => {
        if (selectedReturnTypes.length === 0) {
            return 'Select return types...';
        } else if (selectedReturnTypes.length === returnTypeOptions.length) {
            return 'All types selected';
        } else if (selectedReturnTypes.length === 1) {
            const option = returnTypeOptions.find(opt => opt.value === selectedReturnTypes[0]);
            return option?.label || selectedReturnTypes[0];
        } else {
            return `${selectedReturnTypes.length} types selected`;
        }
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const closeDropdown = () => {
        setDropdownOpen(false);
    };

    const handleRefundAction = async (refundId, action) => {
        if (action === 'reject') {
            // Show rejection modal instead of processing immediately
            setRejectingRefundId(refundId);
            setRejectReason('');
            setShowRejectModal(true);
            return;
        }
        
        if (action === 'approve') {
            // Show approval modal to ask about buyer keeping item
            setApprovingRefundId(refundId);
            setBuyerKeepItem(null);
            setShowApprovalModal(true);
            return;
        }
    };

    const handleApprovalConfirm = async () => {
        if (buyerKeepItem === null) {
            setError('Please select whether the buyer can keep the item');
            return;
        }
        
        setProcessingActions(prev => ({ ...prev, [approvingRefundId]: 'approve' }));
        setShowApprovalModal(false);
        
        try {
            // Find the refund to determine the appropriate decision type
            const refund = refunds.find(r => r.return_id === approvingRefundId);
            if (!refund) {
                throw new Error('Refund not found');
            }
            
            let decision;
            
            // Determine appropriate approval decision based on return type
            switch (refund.return_type) {
                case 'REFUND':
                    decision = 'APPROVE_REFUND';
                    break;
                case 'RETURN_AND_REFUND':
                    decision = 'APPROVE_RETURN';
                    break;
                case 'REPLACEMENT':
                    decision = 'APPROVE_REPLACEMENT';
                    break;
                default:
                    decision = 'APPROVE_REFUND'; // Default fallback
            }
            
            const params = { 
                decision,
                buyer_keep_item: buyerKeepItem
            };
            
            // Call approve endpoint
            const response = await apiClient.post(`/refunds/${approvingRefundId}/approve`, null, { params });
            
            if (response.data?.status === 'success') {
                // Update local state to reflect approval
                setRefunds(prev => prev.map(r => 
                    r.return_id === approvingRefundId 
                        ? { ...r, return_status: 'RETURN_OR_REFUND_REQUEST_SUCCESS' }
                        : r
                ));
                
                // Show success message
                setError(''); // Clear any previous errors
            } else {
                throw new Error(response.data?.message || 'Failed to approve refund');
            }
            
        } catch (err) {
            console.error('Error approving refund:', err);
            setError(`Failed to approve refund: ${err.response?.data?.detail || err.message}`);
        } finally {
            setProcessingActions(prev => ({ ...prev, [approvingRefundId]: null }));
            setApprovingRefundId(null);
            setBuyerKeepItem(null);
        }
    };

    const handleApprovalCancel = () => {
        setShowApprovalModal(false);
        setApprovingRefundId(null);
        setBuyerKeepItem(null);
    };

    const handleRejectConfirm = async () => {
        if (!rejectReason.trim()) {
            setError('Please provide a reason for rejection');
            return;
        }
        
        setProcessingActions(prev => ({ ...prev, [rejectingRefundId]: 'reject' }));
        setShowRejectModal(false);
        
        try {
            // Find the refund to determine the appropriate decision type
            const refund = refunds.find(r => r.return_id === rejectingRefundId);
            if (!refund) {
                throw new Error('Refund not found');
            }
            
            let decision;
            
            // Determine appropriate rejection decision based on return type
            switch (refund.return_type) {
                case 'REFUND':
                    decision = 'REJECT_REFUND';
                    break;
                case 'RETURN_AND_REFUND':
                    decision = 'REJECT_RETURN';
                    break;
                case 'REPLACEMENT':
                    decision = 'REJECT_REPLACEMENT';
                    break;
                default:
                    decision = 'REJECT_REFUND'; // Default fallback
            }
            
            const params = { 
                decision,
                reject_reason: rejectReason.trim()
            };
            
            // Call reject endpoint
            const response = await apiClient.post(`/refunds/${rejectingRefundId}/reject`, null, { params });
            
            if (response.data?.status === 'success') {
                // Update local state to reflect rejection
                setRefunds(prev => prev.map(r => 
                    r.return_id === rejectingRefundId 
                        ? { ...r, return_status: 'REFUND_OR_RETURN_REQUEST_REJECT' }
                        : r
                ));
                
                // Show success message
                setError(''); // Clear any previous errors
            } else {
                throw new Error(response.data?.message || 'Failed to reject refund');
            }
            
        } catch (err) {
            console.error('Error rejecting refund:', err);
            setError(`Failed to reject refund: ${err.response?.data?.detail || err.message}`);
        } finally {
            setProcessingActions(prev => ({ ...prev, [rejectingRefundId]: null }));
            setRejectingRefundId(null);
            setRejectReason('');
        }
    };

    const handleRejectCancel = () => {
        setShowRejectModal(false);
        setRejectingRefundId(null);
        setRejectReason('');
    };

    const getStatusBadge = (status) => {
        const statusInfo = returnStatusMap[status];
        if (!statusInfo) {
            return 'status-badge status-pending';
        }
        
        const typeClasses = {
            'pending': 'status-badge status-pending',
            'approved': 'status-badge status-approved',
            'rejected': 'status-badge status-rejected',
            'processing': 'status-badge status-processing',
            'completed': 'status-badge status-approved',
            'cancelled': 'status-badge status-rejected'
        };
        
        return typeClasses[statusInfo.type] || 'status-badge status-pending';
    };

    const getStatusDisplay = (status) => {
        const statusInfo = returnStatusMap[status];
        return statusInfo ? statusInfo.label : status;
    };

    const getStatusDescription = (status) => {
        const statusInfo = returnStatusMap[status];
        return statusInfo ? statusInfo.description : '';
    };

    const formatAmount = (amount, currency = 'USD') => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) return (
        <div className="refund-orders-container">
            <div className="loading">
                <RefreshCw className="animate-spin" style={{ width: 32, height: 32, margin: '0 auto 16px' }} />
                Loading refund orders...
            </div>
        </div>
    );
    
    if (error) return (
        <div className="refund-orders-container">
            <div className="error">
                <AlertCircle style={{ width: 32, height: 32, margin: '0 auto 16px' }} />
                Error: {error}
            </div>
        </div>
    );

    return (
        <div className="refund-orders-container">
            {/* Header Section */}
            <div className="header-section">
                <GradientHeading>Refund Orders Management</GradientHeading>
                <p className="page-subtitle">
                    Manage and process customer refund requests and return orders
                </p>
                {/* Sample data notice removed */}
            </div>
            
            {/* Filters Section */}
            <div className="controls-section">
                <div className="controls-left">
                    {/* Date Range Filter */}
                    <div className="control-group">
                        <label><Calendar size={16} style={{ display: 'inline', marginRight: 4 }} /> Date Range</label>
                        <select 
                            value={dateRange} 
                            onChange={(e) => setDateRange(e.target.value)}
                            className="select-input"
                        >
                            <option value="yesterday">Yesterday</option>
                            <option value="last_week">Last Week</option>
                            <option value="last_month">Last Month</option>
                            <option value="custom">Custom Range</option>
                        </select>

                        {dateRange === 'custom' && (
                            <div className="custom-date-range">
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    className="date-input"
                                    placeholder="Start Date"
                                />
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    className="date-input"
                                    placeholder="End Date"
                                />
                            </div>
                        )}
                    </div>

                    {/* Return Type Filter */}
                    <div className="control-group">
                        <label><Filter size={16} style={{ display: 'inline', marginRight: 4 }} /> Return Types</label>
                        <div className="return-types-section">
                            <div className="return-types-dropdown">
                                {dropdownOpen && <div className="dropdown-backdrop" onClick={closeDropdown} />}
                                
                                <div 
                                    className={`dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
                                    onClick={toggleDropdown}
                                >
                                    <span className={`dropdown-text ${selectedReturnTypes.length === 0 ? 'placeholder' : ''}`}>
                                        {getDropdownDisplayText()}
                                    </span>
                                    <ChevronDown size={16} className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`} />
                                </div>

                                {dropdownOpen && (
                                    <div className="dropdown-menu">
                                        <div className="dropdown-header">
                                            <button 
                                                onClick={handleSelectAllReturnTypes}
                                                className="dropdown-header-button"
                                            >
                                                Select All
                                            </button>
                                            <button 
                                                onClick={handleClearAllReturnTypes}
                                                className="dropdown-header-button"
                                            >
                                                Clear All
                                            </button>
                                        </div>
                                        <div className="dropdown-options">
                                            {returnTypeOptions.map(option => (
                                                <div 
                                                    key={option.value} 
                                                    className="dropdown-option"
                                                    onClick={() => handleReturnTypeChange(option.value)}
                                                >
                                                    <div className={`dropdown-checkbox ${selectedReturnTypes.includes(option.value) ? 'checked' : ''}`}>
                                                        {selectedReturnTypes.includes(option.value) && (
                                                            <Check size={12} />
                                                        )}
                                                    </div>
                                                    <span className="dropdown-label">{option.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <button 
                    onClick={() => fetchRefunds(false)}
                    className="fetch-button"
                    disabled={fetching}
                >
                    {fetching ? <Loader size="small" /> : <RefreshCw size={16} style={{ marginRight: 8 }} />}
                    {fetching ? 'Loading...' : 'Apply Filters'}
                </button>
            </div>

            {/* Statistics Section */}
            {statistics && Object.keys(statistics).length > 0 && (
                <div className="statistics-section">
                    <h3 className="statistics-title">
                        <DollarSign size={20} style={{ display: 'inline', marginRight: 8 }} />
                        Summary Statistics
                    </h3>
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-value">{statistics.total_count || 0}</div>
                            <div className="stat-label">Total Orders</div>
                        </div>
                        {statistics.total_refund_amount && (
                            <div className="stat-card">
                                <div className="stat-value">
                                    {formatAmount(statistics.total_refund_amount, statistics.currency)}
                                </div>
                                <div className="stat-label">Total Refund Amount</div>
                            </div>
                        )}
                        {statistics.status_counts && Object.keys(statistics.status_counts).length > 0 && (
                            <div className="stat-card">
                                <div className="stat-label" style={{ marginBottom: 12 }}>Status Breakdown</div>
                                <div className="status-breakdown">
                                    {Object.entries(statistics.status_counts).map(([status, count]) => (
                                        <div key={status} className="status-item" title={getStatusDescription(status)}>
                                            <span className="status-item-label">{getStatusDisplay(status)}</span>
                                            <span className="status-item-value">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {statistics.return_type_counts && Object.keys(statistics.return_type_counts).length > 0 && (
                            <div className="stat-card">
                                <div className="stat-label" style={{ marginBottom: 12 }}>Type Breakdown</div>
                                <div className="status-breakdown">
                                    {Object.entries(statistics.return_type_counts).map(([type, count]) => (
                                        <div key={type} className="status-item">
                                            <span className="status-item-label">{type.replace('_', ' ')}</span>
                                            <span className="status-item-value">{count}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Refunds List */}
            <div className="refunds-section">
                <div className="refunds-header">
                    <h3 className="refunds-title">
                        <Package size={20} style={{ display: 'inline', marginRight: 8 }} />
                        Refund Orders
                    </h3>
                    <div className="refunds-count">{refunds.length}</div>
                </div>

                <div className="table-container">
                    <table className="refunds-table">
                        <thead>
                            <tr>
                                <th>Refund Details</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Reason</th>
                                <th>Amount</th>
                                <th>Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {refunds.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="no-data-cell">
                                        <div className="no-data">
                                            <Package className="no-data-icon" />
                                            <p>No refund orders found for the selected criteria.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                refunds.map(refund => (
                                    <tr key={refund.return_id}>
                                        <td>
                                            <div className="refund-details">
                                                <div className="refund-id">#{refund.return_id}</div>
                                                <div className="refund-meta">
                                                    Order: {refund.order_id}<br/>
                                                    Customer: {refund.customer_name || 'N/A'}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="status-badge status-processing">
                                                {refund.return_type?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td>
                                            <span 
                                                className={getStatusBadge(refund.return_status)}
                                                title={getStatusDescription(refund.return_status)}
                                            >
                                                {getStatusDisplay(refund.return_status)}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="reason-text" title={refund.return_reason_text || refund.return_reason}>
                                                {refund.return_reason_text || refund.return_reason || 'N/A'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="amount-display">
                                                {refund.refund_amount ? 
                                                    formatAmount(refund.refund_amount.refund_total, refund.refund_amount.currency) : 
                                                    'N/A'
                                                }
                                            </div>
                                        </td>
                                        <td>
                                            <div className="date-display">
                                                {formatDate(refund.create_time)}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="actions-cell">
                                                {(refund.return_status === 'RETURN_OR_REFUND_REQUEST_PENDING' || 
                                                  refund.return_status === 'REPLACEMENT_REQUEST_PENDING') && (
                                                    <>
                                                        <button
                                                            onClick={() => handleRefundAction(refund.return_id, 'approve')}
                                                            disabled={processingActions[refund.return_id]}
                                                            className="action-button approve-button"
                                                        >
                                                            {processingActions[refund.return_id] === 'approve' ? (
                                                                <Loader size="small" />
                                                            ) : (
                                                                <CheckCircle size={12} style={{ marginRight: 4 }} />
                                                            )}
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => handleRefundAction(refund.return_id, 'reject')}
                                                            disabled={processingActions[refund.return_id]}
                                                            className="action-button reject-button"
                                                        >
                                                            {processingActions[refund.return_id] === 'reject' ? (
                                                                <Loader size="small" />
                                                            ) : (
                                                                <XCircle size={12} style={{ marginRight: 4 }} />
                                                            )}
                                                            Reject
                                                        </button>
                                                    </>
                                                )}
                                                {!(refund.return_status === 'RETURN_OR_REFUND_REQUEST_PENDING' || 
                                                   refund.return_status === 'REPLACEMENT_REQUEST_PENDING') && (
                                                    <span 
                                                        className="action-button" 
                                                        style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                        title={getStatusDescription(refund.return_status)}
                                                    >
                                                        <Clock size={12} style={{ marginRight: 4 }} />
                                                        {getStatusDisplay(refund.return_status)}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Approval Modal */}
            {showApprovalModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Approve Refund Request</h3>
                            <button 
                                className="modal-close"
                                onClick={handleApprovalCancel}
                            >
                                <XCircle size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Please confirm if you want to approve this refund request:</p>
                            <div className="approval-question">
                                <p className="question-text">Can the buyer keep the item?</p>
                                <div className="radio-options">
                                    <label className="radio-option">
                                        <input
                                            type="radio"
                                            name="buyerKeepItem"
                                            checked={buyerKeepItem === true}
                                            onChange={() => setBuyerKeepItem(true)}
                                        />
                                        <span className="radio-label">Yes, buyer can keep the item</span>
                                    </label>
                                    <label className="radio-option">
                                        <input
                                            type="radio"
                                            name="buyerKeepItem"
                                            checked={buyerKeepItem === false}
                                            onChange={() => setBuyerKeepItem(false)}
                                        />
                                        <span className="radio-label">No, buyer must return the item</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary"
                                onClick={handleApprovalCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-success"
                                onClick={handleApprovalConfirm}
                                disabled={buyerKeepItem === null || processingActions[approvingRefundId]}
                            >
                                {processingActions[approvingRefundId] ? (
                                    <>
                                        <Loader size="small" />
                                        Approving...
                                    </>
                                ) : (
                                    'Approve Refund'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rejection Modal */}
            {showRejectModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Reject Refund Request</h3>
                            <button 
                                className="modal-close"
                                onClick={handleRejectCancel}
                            >
                                <XCircle size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Please provide a reason for rejecting this refund request:</p>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter rejection reason..."
                                className="rejection-reason-textarea"
                                rows={4}
                                maxLength={500}
                            />
                            <div className="character-count">
                                {rejectReason.length}/500 characters
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary"
                                onClick={handleRejectCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn-danger"
                                onClick={handleRejectConfirm}
                                disabled={!rejectReason.trim() || processingActions[rejectingRefundId]}
                            >
                                {processingActions[rejectingRefundId] ? (
                                    <>
                                        <Loader size="small" />
                                        Rejecting...
                                    </>
                                ) : (
                                    'Reject Refund'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RefundOrders;